'use client'

import React, {useMemo} from 'react'

/**
 * @typedef {{
 * cardInformation: {
 *      cardNumber: boolean,
 *      cardCVV: boolean,
 *      cardYear: boolean,
 *      cardMonth: boolean,
 * },
 * isValidCard: boolean,
 * updateValidation: (field: 'cardNumber' | 'cardCVV' | 'cardYear' | 'cardMonth', isValid: boolean) => void,
 * resetValidation: () => void,
 * }} PaymentCardContext
 */

const PaymentCardContext = React.createContext(null)

const initialCardValidation = {
  cardNumber: false,
  cardCVV: false,
  cardYear: false,
  cardMonth: false,
}

export function PaymentCardProvider({children}) {
  const [cardValidation, setCardValidation] = React.useState(
    initialCardValidation,
  )

  const updateValidation = React.useCallback((field, value) => {
    setCardValidation(prevState => ({
      ...prevState,
      [field]: value,
    }))
  }, [])

  const resetCard = React.useCallback(() => {
    setCardValidation(initialCardValidation)
  }, [])

  const isValidCard = useMemo(() => {
    return Boolean(
      cardValidation.cardNumber &&
        cardValidation.cardCVV &&
        cardValidation.cardYear &&
        cardValidation.cardMonth,
    )
  }, [cardValidation])

  const value = React.useMemo(
    () => ({
      cardValidation,
      isValidCard,
      updateValidation,
      resetCard,
    }),
    [cardValidation, isValidCard, updateValidation, resetCard],
  )

  return (
    <PaymentCardContext.Provider value={value}>
      {children}
    </PaymentCardContext.Provider>
  )
}

/**
 * @returns {PaymentCardContext}
 */
export function usePaymentCard() {
  const context = React.useContext(PaymentCardContext)
  if (!context) {
    throw new Error(
      "'usePaymentCard' can't be called outside 'PaymentCardProvider'",
    )
  }
  return context
}
